/**
 * ElektrikciImzaModal.jsx - staff record that the stand's own electrician signed the Elektrik taahhütname on the
 * fairground (backlog #217 / #506, backend #505): POST /projects/{id}/taahhutnameler/elektrik/elektrikci.
 *
 * The electrician is a kişi hired at this fair by the stand's participant or decorator (the two rosters are offered through
 * PersonelSecici), or a typed name when he has no card (a taşeron's man too: the backend only takes a kişi of the stand's
 * own companies). The date, a note and the photo of the signed paper are optional; the photo may also come later. Mounted
 * only while it is open, so the form starts from the props every time.
 */
import React, { useState } from "react";
import {
  Alert, Button, ButtonGroup, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import PersonelSecici from "../Kisi/PersonelSecici";
import TarihGirdisi from "../Common/TarihGirdisi";
import { elektrikciFormHatasi, elektrikciGovdesi } from "./taahhutnameYardimci";

function bugun() {
  return new Date().toISOString().slice(0, 10);
}

export default function ElektrikciImzaModal({ isOpen, toggle, header, onKaydet }) {
  const { t } = useTranslation("projeler");
  const firmalar = [
    header?.katilimciFirmaId ? { id: header.katilimciFirmaId, ad: header.katilimciFirmaAdi, tur: "katilimci" } : null,
    header?.dekoratorFirmaId && header.dekoratorFirmaId !== header.katilimciFirmaId
      ? { id: header.dekoratorFirmaId, ad: header.dekoratorFirmaAdi, tur: "dekorator" } : null,
  ].filter(Boolean);

  const [mod, setMod] = useState(firmalar.length > 0 ? "kisi" : "ad");   // "kisi" | "ad"
  const [firmaId, setFirmaId] = useState(firmalar[0]?.id ?? null);
  const [personId, setPersonId] = useState(null);
  const [ad, setAd] = useState("");
  const [imzaTarihi, setImzaTarihi] = useState(bugun);
  const [not, setNot] = useState("");
  const [foto, setFoto] = useState(null);
  const [hata, setHata] = useState(null);
  const [kaydediyor, setKaydediyor] = useState(false);

  async function kaydet() {
    const girdi = { personId: mod === "kisi" ? personId : null, ad: mod === "ad" ? ad : "", imzaTarihi, not, foto };
    const refus = elektrikciFormHatasi(girdi);
    if (refus) {
      setHata(t(refus));
      return;
    }
    setKaydediyor(true);
    setHata(null);
    try {
      await onKaydet(elektrikciGovdesi(girdi));
      toggle();
    } catch (e) {
      setHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" data-testid="elektrikci-modal">
      <ModalHeader toggle={toggle}>{t("taahhutnameDurumu.elektrikci.baslik")}</ModalHeader>
      <ModalBody>
        <p className="text-muted small">{t("taahhutnameDurumu.elektrikci.aciklama")}</p>

        <ButtonGroup size="sm" className="mb-3">
          <Button color="primary" outline={mod !== "kisi"} disabled={firmalar.length === 0} onClick={() => setMod("kisi")} data-testid="elektrikci-mod-kisi">
            {t("taahhutnameDurumu.elektrikci.kayitliKisi")}
          </Button>
          <Button color="primary" outline={mod !== "ad"} onClick={() => setMod("ad")} data-testid="elektrikci-mod-ad">
            {t("taahhutnameDurumu.elektrikci.adYaz")}
          </Button>
        </ButtonGroup>

        {mod === "kisi" ? (
          <>
            {firmalar.length > 1 && (
              <ButtonGroup size="sm" className="mb-2 d-flex">
                {firmalar.map((f) => (
                  <Button
                    key={f.id} color="secondary" outline={firmaId !== f.id}
                    onClick={() => { setFirmaId(f.id); setPersonId(null); }}
                  >
                    {t(`taahhutnameDurumu.elektrikci.${f.tur}`)}{f.ad ? `: ${f.ad}` : ""}
                  </Button>
                ))}
              </ButtonGroup>
            )}
            <PersonelSecici
              firmaId={firmaId}
              secili={personId != null ? [personId] : []}
              onChange={(ids) => setPersonId(ids[0] ?? null)}
              tekli
            />
          </>
        ) : (
          <FormGroup>
            <Label className="small" for="elektrikci-ad">{t("taahhutnameDurumu.elektrikci.adSoyad")}</Label>
            <Input id="elektrikci-ad" value={ad} maxLength={120} onChange={(e) => setAd(e.target.value)} data-testid="elektrikci-ad" />
          </FormGroup>
        )}

        <div className="row g-2 mt-1">
          <div className="col-md-4">
            <Label className="small" for="elektrikci-tarih">{t("taahhutnameDurumu.elektrikci.imzaTarihi")}</Label>
            <TarihGirdisi id="elektrikci-tarih" bsSize={null} value={imzaTarihi} onChange={setImzaTarihi} />
          </div>
          <div className="col-md-8">
            <Label className="small" for="elektrikci-not">{t("taahhutnameDurumu.elektrikci.not")}</Label>
            <Input id="elektrikci-not" value={not} maxLength={300} onChange={(e) => setNot(e.target.value)} />
          </div>
          <div className="col-12">
            <Label className="small" for="elektrikci-foto">{t("taahhutnameDurumu.elektrikci.fotoIsteBagli")}</Label>
            <Input id="elektrikci-foto" type="file" accept="image/*,application/pdf" onChange={(e) => setFoto(e.target.files?.[0] ?? null)} />
          </div>
        </div>

        {hata && <Alert color="danger" className="mt-3 mb-0" data-testid="elektrikci-hata">{hata}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>{t("common:iptal")}</Button>
        <Button color="primary" disabled={kaydediyor} onClick={kaydet} data-testid="elektrikci-kaydet">
          {kaydediyor ? <Spinner size="sm" /> : t("taahhutnameDurumu.elektrikci.kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
